import {
  ListItem,
  ListItemText,
  TableList,
} from 'app/components/ChakraTableList';
import { Badge, Skeleton, SkeletonCircle } from '@chakra-ui/react';
import { format } from 'date-fns';
import { ReactComponent as NotificationIcon } from 'resources/images/notifications.svg';
import { ChakraEmptyList } from 'app/components/Core';
import { dateFormats } from 'app/constants';
import { useInfiniteLoader } from 'app/utils/hooks';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ChatChannelListItem } from 'types/chat.types';
import { fetchChatChannelsEffect } from 'app/store/Chat/effects';
import { getChatChannelsNextLink } from 'app/store/Chat/selectors';
import { SongCover } from 'app/components/Song/SongCover';
import { setSongChatBarSettings } from 'app/store/Settings/slice';
import { useAppDispatch } from 'app/store';
import { useNavigate } from 'react-router-dom';
import { routes } from 'app/routing/routesConfig';

export interface ChatChannelsListComponentProps {
  loading: boolean;
  channels: ChatChannelListItem[];
}

export const ChatChannelsListComponent = ({
  channels,
  loading,
}: ChatChannelsListComponentProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [, infiniteScrollProps, loadingMore] = useInfiniteLoader({
    dataLength: channels.length,
    nextLinkSelector: getChatChannelsNextLink,
    fetchNext: (link) => fetchChatChannelsEffect({ next: link }),
  });

  if (!channels.length && loading) {
    return (
      <ChakraEmptyList
        icon={<NotificationIcon role="img" />}
        title="You don't have any messages yet"
        primaryText="Don't worry - any new activity will be posted here. In the mean time, invite a friend to Session Studio, or start a new song."
      />
    );
  }

  return (
    <InfiniteScroll {...infiniteScrollProps}>
      <TableList
        loadingOptions={{
          loading,
          loadingMore,
          repeat: 3,
          loader: {
            avatar: (
              <SkeletonCircle
                width="40px"
                height="40px"
                borderBottomLeftRadius="10%"
                borderTopLeftRadius="10%"
                data-testid="messages-loader"
              />
            ),
            title: <Skeleton width="100%" height="24px" />,
          },
        }}
        columns={[
          { key: 'avatar', name: '', accessory: true },
          {
            key: 'title',
            name: '',
          },
          { key: 'status', name: '', accessory: true, width: 40 },
        ]}
        onRowClick={({ rowData }) => {
          const songId = rowData.parentGuid;
          navigate(routes.song.path.replace(':guid', songId), {
            state: { openChat: true },
          });
          dispatch(setSongChatBarSettings({ songId }));
        }}
        rows={channels.map((channel) => {
          const titleTooltip = `${channel.title}\n${
            channel.latestMessage?.text || ''
          }`;
          const { displayName } =
            channel.latestMessage?.createdBy.profileCard || {};
          const { unreadMessageCount } = channel;
          const time = format(
            new Date(channel.created.iso),
            dateFormats.LIST_FORMAT
          );
          return {
            rowData: {
              parentGuid: channel.parentGuid,
            },
            clickable: true,
            avatar: <SongCover size="medium" status={channel.status} />,
            title: (
              <ListItemText
                primary={
                  <>
                    <b data-testid="song-title" title={titleTooltip}>
                      {channel.title}&nbsp;
                    </b>
                    <span data-testid="message" title={titleTooltip}>
                      {channel.latestMessage?.text}
                    </span>
                  </>
                }
                secondary={displayName ? `By ${displayName} · ${time}` : time}
                secondaryTextProps={{
                  'data-testid': 'sent-by',
                  color: 'textSubdued',
                  fontSize: 'xs',
                }}
              />
            ),
            status: (
              <ListItem>
                {unreadMessageCount ? (
                  <Badge data-testid="unread-message-count" bg="B.80">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </Badge>
                ) : null}
              </ListItem>
            ),
          };
        })}
      />
    </InfiniteScroll>
  );
};
